import React, { useEffect, useMemo, useRef, useState } from "react";
import "./OperatorSearchSelect.css";

// A searchable drop-in replacement for a plain <select> of operators/
// admins. With a lot of them, scrolling a native <select> to find one
// is painful — this filters the list by name OR mobile number as you
// type instead.
//
// Props:
//   options     — [{ id, operatorName, phoneNumber, ...anything }]
//   value       — the currently selected id (or "" / null / undefined)
//   onChange    — (id, option) => void. id is null when cleared.
//   placeholder — shown when nothing is typed/selected
//   disabled    — same as a native <select disabled>
//
// This component only handles display + filtering + picking a value —
// callers keep their own onChange logic (building operatorDetails
// strings, resetting dependent state, etc.) exactly as before; only the
// *trigger* for that logic changes from a native onChange event to a
// plain (id, option) callback.
const OperatorSearchSelect = ({
  options = [],
  value,
  onChange,
  placeholder = "-- Select Operator/Admin --",
  disabled = false,
  className = "",
}) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const wrapperRef = useRef(null);

  const selectedOption = options.find(
    (o) => o != null && String(o.id) === String(value)
  );

  // Close the dropdown on outside click — otherwise it stays open once
  // opened until something inside it is clicked.
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
        setHighlightIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filtered = useMemo(() => {
    const text = query.trim().toLowerCase();
    if (!text) return options;
    return options.filter((o) => {
      const name = (o?.operatorName || "").toLowerCase();
      const phone = (o?.phoneNumber || "").toLowerCase();
      return name.includes(text) || phone.includes(text);
    });
  }, [options, query]);

  const selectOption = (option) => {
    onChange(option ? option.id : null, option || null);
    setQuery("");
    setOpen(false);
    setHighlightIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
      setOpen(true);
      return;
    }
    if (!open) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightIndex >= 0 && filtered[highlightIndex]) {
        selectOption(filtered[highlightIndex]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
      setQuery("");
      setHighlightIndex(-1);
    }
  };

  return (
    <div className="operator-search-select" ref={wrapperRef}>
      <input
        type="text"
        className={`operator-search-select-input ${className}`}
        placeholder={
          selectedOption
            ? `${selectedOption.operatorName} - ${selectedOption.phoneNumber}`
            : placeholder
        }
        value={query}
        disabled={disabled}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setHighlightIndex(-1);
        }}
        onKeyDown={handleKeyDown}
        autoComplete="off"
      />

      {selectedOption && !query && !disabled && (
        <button
          type="button"
          className="operator-search-select-clear"
          onClick={() => selectOption(null)}
          tabIndex={-1}
          aria-label="Clear selection"
        >
          ×
        </button>
      )}

      {open && !disabled && (
        <div className="operator-search-select-dropdown">
          {filtered.length === 0 ? (
            <div className="operator-search-select-empty">No matches</div>
          ) : (
            filtered.map((o, index) => (
              <div
                key={o.id}
                className={`operator-search-select-option ${
                  String(o.id) === String(value) ? "selected" : ""
                } ${index === highlightIndex ? "highlighted" : ""}`}
                // onMouseDown (not onClick) fires before the input's
                // outside-click handler can close the dropdown first.
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectOption(o);
                }}
                onMouseEnter={() => setHighlightIndex(index)}
              >
                <span className="operator-search-select-name">{o.operatorName}</span>
                <span className="operator-search-select-phone">{o.phoneNumber}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default OperatorSearchSelect;
